import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, GraduationCap, Award, LineChart, Sparkles, CheckCircle2 } from "lucide-react";
import { Logo, Eyebrow, TripleSlash, Slash } from "@/components/brand/Logo";
import { IMAGES } from "@/lib/constants";
import { ShowcaseStrip } from "@/components/landing/ShowcaseStrip";

const PrimaryBtn = ({ to, children, testId, light }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-colors duration-200 ${
      light ? "bg-white text-lasso-ink hover:bg-lasso-sky" : "bg-lasso-ink text-white hover:bg-lasso-navy"
    }`}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
  </Link>
);

const GhostBtn = ({ to, children, testId, light }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-sm font-semibold transition-colors duration-200 ${
      light ? "border-white/30 text-white hover:border-white hover:bg-white/10" : "border-lasso-ink/20 text-lasso-ink hover:border-lasso-ink hover:bg-white"
    }`}
  >
    {children}
  </Link>
);

function Nav() {
  return (
    <header className="sticky top-0 z-40 border-b border-lasso-line/70 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="nav-brand-logo"><Logo size="sm" /></Link>
        <nav className="hidden items-center gap-8 text-sm font-medium text-zinc-600 md:flex">
          <a href="#features" className="transition-colors hover:text-lasso-ink" data-testid="nav-link-features">Features</a>
          <a href="#how" className="transition-colors hover:text-lasso-ink" data-testid="nav-link-how">How it works</a>
          <a href="#roles" className="transition-colors hover:text-lasso-ink" data-testid="nav-link-roles">Roles</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="rounded-full px-4 py-2 text-sm font-semibold transition-colors hover:bg-lasso-tint" data-testid="nav-sign-in-btn">Sign in</Link>
          <Link to="/request-access" className="hidden rounded-full bg-lasso-ink px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-lasso-navy sm:inline-flex" data-testid="nav-request-access-btn">Request access</Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-white">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-20 pt-16 lg:grid-cols-[1.05fr_1fr] lg:pb-28 lg:pt-24">
        <div className="fade-up">
          <Eyebrow>Lasso Communities · Internal training</Eyebrow>
          <h1 className="mt-7 font-display text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-[3.35rem]" data-testid="landing-hero-title">
            Train every community{" "}
            <span className="relative whitespace-nowrap">
              <span className="relative z-10">the Lasso way.</span>
              <span aria-hidden="true" className="absolute -bottom-1 left-1 right-2 z-0 h-3 rounded-[3px] bg-lasso-sky/70 slant sm:h-4" />
            </span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
            Lasso Hub is the single home for our standard operating procedures and training — so a leasing conversation in Troy, a water shutoff in Louisville and a move-in in Raeford all happen the same, professional way.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <PrimaryBtn to="/login" testId="landing-hero-sign-in-button">Sign in</PrimaryBtn>
            <GhostBtn to="/request-access" testId="landing-hero-request-access-button">Request access</GhostBtn>
          </div>
          <dl className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-lasso-line pt-8">
            {[["41+", "Communities"], ["4,030+", "Homesites"], ["7", "States served"]].map(([n, l]) => (
              <div key={l}>
                <dt className="font-display text-2xl font-bold sm:text-3xl">{n}</dt>
                <dd className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">{l}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative overflow-hidden rounded-[28px]">
            <img src={IMAGES.hero} alt="Community manager and maintenance tech in a Lasso community" className="aspect-[4/5] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]" />
          </div>
          <div className="absolute -left-4 bottom-8 w-72 rounded-2xl border border-lasso-line bg-white/95 p-5 shadow-[0_20px_50px_rgba(11,11,12,0.12)] backdrop-blur-md sm:-left-10" data-testid="landing-hero-sop-card">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-lasso-deep">Maintenance · v1</span>
              <TripleSlash />
            </div>
            <p className="mt-3 font-display text-sm font-bold">Emergency Water Leak Response</p>
            <div className="mt-4 space-y-2.5">
              {["Assess and secure the scene", "Shut off water at the closest valve", "Notify affected residents"].map((s, i) => (
                <div key={s} className="flex items-center gap-3 text-xs">
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${i < 2 ? "bg-lasso-ink text-white" : "border border-lasso-line text-zinc-500"}`}>{i + 1}</span>
                  <span className={i < 2 ? "font-medium" : "text-zinc-500"}>{s}</span>
                </div>
              ))}
            </div>
          </div>
          <div aria-hidden="true" className="absolute -right-6 -top-6 hidden h-24 w-40 rounded-xl bg-lasso-sky slant lg:block" style={{ zIndex: -1 }} />
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: BookOpen, title: "SOP library", text: "Every procedure written once, step by step, searchable by category and role — the current version, always.", status: "Live now", span: "lg:col-span-2 lg:row-span-2", feature: true },
  { icon: GraduationCap, title: "Courses & quizzes", text: "Short lessons with video and PDFs, plus quizzes with a pass mark and retakes.", status: "Phase 2" },
  { icon: Award, title: "Certificates", text: "Downloadable certificates when a team member completes a required course.", status: "Phase 2" },
  { icon: LineChart, title: "Progress tracking", text: "See completion by person, community and region — and who's overdue.", status: "Phase 2" },
  { icon: Sparkles, title: "AI SOP assistant", text: "Ask a question in plain English and get an answer sourced only from Lasso SOPs, with the SOP cited.", status: "Phase 3" },
];

function Features() {
  return (
    <section id="features" className="bg-lasso-paper py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <Eyebrow>What's inside</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight sm:text-4xl">One place for how we run our communities.</h2>
        </div>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`group relative overflow-hidden rounded-3xl border p-7 transition-transform duration-300 hover:-translate-y-1 ${
                f.feature ? "border-lasso-ink bg-lasso-ink text-white" : "border-lasso-line bg-white"
              } ${f.span || ""}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="flex items-center justify-between">
                <f.icon className={`h-6 w-6 ${f.feature ? "text-lasso-sky" : "text-lasso-deep"}`} />
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                  f.status === "Live now" ? "bg-lasso-sky text-lasso-ink" : f.feature ? "bg-white/10" : "bg-lasso-tint text-lasso-deep"
                }`}>{f.status}</span>
              </div>
              <h3 className={`mt-8 font-display font-bold ${f.feature ? "text-2xl sm:text-3xl" : "text-lg"}`}>{f.title}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${f.feature ? "max-w-md text-white/70 sm:text-base" : "text-zinc-600"}`}>{f.text}</p>
              {f.feature && (
                <div className="mt-10 grid gap-2 sm:grid-cols-2">
                  {["Leasing", "Rent Collection", "Maintenance", "Move-In/Move-Out", "Compliance & Safety", "Resident Relations"].map((c) => (
                    <div key={c} className="flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-sm text-white/80"><Slash /> {c}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  ["Request access", "Team members request an account with their role and community."],
  ["Admin approves", "An admin assigns the role, community or region, and sign-in method."],
  ["Follow the SOPs", "Every procedure for your role, in a clean step-by-step reader."],
  ["Train & certify", "Assigned courses, quizzes and certificates (coming in Phase 2)."],
];

function HowItWorks() {
  return (
    <section id="how" className="bg-white py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight sm:text-4xl">From day one to fully trained.</h2>
          <img src={IMAGES.field} alt="Maintenance tech checking a water shutoff" className="mt-10 aspect-[4/3] w-full rounded-3xl object-cover" />
        </div>
        <ol className="grid content-center gap-0">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="group flex gap-6 border-b border-lasso-line py-8 first:pt-0" data-testid={`landing-how-step-${i + 1}`}>
              <span className="font-display text-4xl font-bold text-lasso-sky transition-colors duration-300 group-hover:text-lasso-ink">0{i + 1}</span>
              <div>
                <h3 className="font-display text-lg font-bold">{t}</h3>
                <p className="mt-2 text-zinc-600">{d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const ROLE_CARDS = [
  ["Admin", "Corporate leadership & operations", ["Approve access & manage users", "Publish and version SOPs", "Manage regions & communities"]],
  ["Regional Manager", "Oversees a cluster of communities", ["View team across the region", "Reference every SOP", "Training oversight (Phase 2)"]],
  ["Community Manager", "Runs the community day to day", ["Leasing & rent collection SOPs", "Move-in / move-out playbooks", "Resident relations standards"]],
  ["Maintenance Tech", "Keeps homes and homesites safe", ["Work order & emergency SOPs", "Make-ready checklists", "Safety & compliance steps"]],
];

function Roles() {
  return (
    <section id="roles" className="relative overflow-hidden bg-lasso-ink py-24 text-white grain lg:py-32">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Eyebrow dark>Built for every role</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight sm:text-4xl">The right SOPs for the right people.</h2>
          </div>
          <TripleSlash />
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {ROLE_CARDS.map(([role, sub, items]) => (
            <div key={role} className="bg-lasso-ink p-8 transition-colors duration-300 hover:bg-[#141416]" data-testid={`landing-role-${role.toLowerCase().replace(/\s+/g, "-")}`}>
              <h3 className="font-display text-lg font-bold">{role}</h3>
              <p className="mt-2 text-sm text-white/50">{sub}</p>
              <ul className="mt-8 space-y-3">
                {items.map((it) => (
                  <li key={it} className="flex gap-3 text-sm text-white/80"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-lasso-sky" />{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative overflow-hidden rounded-[32px] bg-lasso-sky px-8 py-16 sm:px-16">
          <div aria-hidden="true" className="absolute -right-10 top-0 h-full w-1/3 bg-white/25 slant" />
          <div aria-hidden="true" className="absolute right-1/4 top-0 h-full w-16 bg-white/15 slant" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl font-bold tracking-tight text-lasso-ink sm:text-4xl">Ready to run your community the Lasso way?</h2>
            <p className="mt-5 text-lasso-ink/70">Sign in with your work account, or request access and an admin will get you set up.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <PrimaryBtn to="/login" testId="landing-cta-sign-in-button">Sign in</PrimaryBtn>
              <GhostBtn to="/request-access" testId="landing-cta-request-access-button">Request access</GhostBtn>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-lasso-ink py-14 text-white">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 px-6 md:flex-row md:items-center">
        <Logo size="sm" inverted testId="footer-logo" />
        <p className="text-sm text-white/50">© {new Date().getFullYear()} Lasso Communities · Internal use only · Equal Housing Opportunity</p>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-white text-lasso-ink" data-testid="landing-page">
      <Nav />
      <Hero />
      <ShowcaseStrip />
      <Features />
      <HowItWorks />
      <Roles />
      <CTA />
      <Footer />
    </div>
  );
}
